import { keepPreviousData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { AppID } from '@playq/octopus-common';
import { TeamMessagesSnapshot } from '@playq/octopus2-tgservices';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { TeamIdWithTemplateId } from '@playq/services-teams';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { services2 } from '/api/services2';

import { calculateTotalAttachment } from '../helpers';

export const refetchInterval = 5000;

export const messageKeys: QueryKey = ['chats', 'messages', 'snapshot'];

export const useQueryTeamMessagesSnapshot = (
  appID: AppID,
  teamId: TeamIdWithTemplateId,
  iterator = new OffsetLimit({ offset: 0, limit: 10 }),
  options?: IUseQueryOptionsExtended<TeamMessagesSnapshot[] | undefined, GenericFailure | Error>
) => {
  const queryClient = useQueryClient();
  const keys = useMemo(() => messageKeys.concat(teamId, iterator), [teamId, iterator]);
  const res = useEitherQuery(
    keys,
    () => services2.teamsChatLookupService.messagesSnapshotQueryByTeam(appID, teamId, iterator),
    {
      placeholderData: keepPreviousData,
      ...options,
      staleTime: refetchInterval,
    }
  );

  const { nextIterator, preFetchData, nextKeys, setPreFetchData } = useQueryPrefetch({
    keys,
    enabled: true,
    total: res.data?.length,
    args: [appID, teamId, iterator],
    serviceMethod: services2.teamsChatLookupService.messagesSnapshotQueryByTeam.bind(services2.teamsChatLookupService),
    hasMore: res.data?.length === iterator.limit ? true : undefined,
  });
  useEffect(() => {
    if (nextKeys && res.data?.length === iterator.limit) {
      setPreFetchData(queryClient.getQueryData(nextKeys));
    }
    return () => setPreFetchData(undefined);
  }, [iterator.limit, nextKeys, queryClient, res.data?.length, setPreFetchData]);

  const total = useMemo(
    () =>
      calculateTotalAttachment(nextIterator, res.data?.length ?? 0, iterator, preFetchData?.length ?? iterator.limit),
    [nextIterator, res.data?.length, iterator, preFetchData?.length]
  );

  return {
    ...res,
    keys,
    total,
  };
};
